import { FC, useContext, useMemo } from 'react';
import { useNavigation } from 'react-navi';
import { AuthContext } from '@repo-level/core-front';

import { useTheme } from 'hooks/theme';
import { BonanzaStatus } from 'helpers/bonanza';
import { BonanzaContext } from 'contexts/bonanza';
import { PERCENT_BREACKPOINTS_BONANZA } from 'modules/bonanza/components/BoxSection/helpers';
import { SmallProgressBar } from 'components/progressBars/SmallProgressBar';

import { ScBonanzaPromoSection } from './styled';

export interface BonanzaPromoSectionProps {
  isMobile?: boolean;
  onLinkClick?: () => void;
}

export const BonanzaPromoSection: FC<BonanzaPromoSectionProps> = ({
  isMobile = false,
  onLinkClick,
}) => {
  const {
    bonanzaReward,
    bonanzaData,
    bonanzaLevelItem,
    userBonanzaGroup,
  } = useContext(BonanzaContext);
  const { isAuthenticated } = useContext(AuthContext);

  const { navigate } = useNavigation();
  const { images } = useTheme();

  const { isLockedPromo, isBonusIssued } = useMemo(
    () => ({
      isLockedPromo: userBonanzaGroup === BonanzaStatus.PROMO_LOCKED,
      isBonusIssued: userBonanzaGroup === BonanzaStatus.BONUS_ISSUED,
    }),
    [userBonanzaGroup]
  );

  const imgSrc = useMemo(() => {
    if (!bonanzaLevelItem) return '';
    const { labelMob, label } = bonanzaLevelItem;
    if (isAuthenticated) {
      return isMobile ? labelMob?.url : label?.url;
    }
    return isMobile ? bonanzaData?.menu?.unloggedMob.url : label?.url;
  }, [isAuthenticated, isMobile, bonanzaLevelItem, bonanzaData]);

  const imgConfig = useMemo(
    () => ({
      src: imgSrc,
      width: isMobile ? 56 : 128,
      height: isMobile ? 56 : 32,
    }),
    [isMobile, imgSrc]
  );
  const progressbar = (
    <SmallProgressBar
      userProgress={bonanzaLevelItem?.progressPercent || 0}
      config={{
        dotsCount: PERCENT_BREACKPOINTS_BONANZA.length,
      }}
    />
  );

  const isPromoAvailable = isAuthenticated && !isLockedPromo && !isBonusIssued;
  if (!bonanzaData || !bonanzaLevelItem) return null;

  const {
    bg,
    yourProgress,
    yourReward,
    unLoggedLabelText,
    returnToStartFresh,
    openBonus,
  } = bonanzaData.menu;

  return (
    <ScBonanzaPromoSection
      onClick={() => {
        onLinkClick && onLinkClick();
        navigate('/bonanza');
      }}
      isMobile={isMobile}
      bgMenu={bg.url}
    >
      <div className="info">
        <div className="addons">
          {imgSrc && <img {...imgConfig} alt="logo" />}
          {isMobile && isPromoAvailable && (
            <div className="text">
              <span>
                <b>{yourProgress}:</b>
                {bonanzaLevelItem.progressPercent}%
              </span>
              <span>
                <b>{yourReward}:</b>
                {bonanzaReward}
              </span>
              {isPromoAvailable && isMobile && progressbar}
            </div>
          )}
          {!isAuthenticated ? (
            <p className="title">{unLoggedLabelText}</p>
          ) : isLockedPromo ? (
            <p className="title">{returnToStartFresh}</p>
          ) : isBonusIssued ? (
            <p className="title">{openBonus}</p>
          ) : null}
          {isMobile && <images.arrowRightSmallIcon.component />}
        </div>
      </div>

      {isPromoAvailable && !isMobile && (
        <>
          {progressbar}
          <p className="under-progress-text">
            {bonanzaLevelItem.progressPercent}%
          </p>
        </>
      )}
    </ScBonanzaPromoSection>
  );
};
